import { HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, mapTo, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import {
    ApiFragment,
    ApiQuery,
    CustomHttpParamEncoder,
    forkJoinAlwaysEmit,
    FragmentId,
    Identifier,
    MediaObjectId,
    Metadata,
    Paginated,
    PaginatedResults,
    PaginationInfo,
    RecordId,
    RecordSearchOptions,
    RecordSearchRequest,
    RejectionList,
    ResponsePaginationInfo,
    SearchParamsPostBody,
    ShAbstractHttpService,
    ShPaginationService,
    ShRecordService,
    SortInfo
} from '@stylehaven/components';
import { MiddlewareService } from '../middleware/middleware.abstract.service';

@Injectable({ providedIn: 'root' })
export class SearchService implements ShRecordService {
    constructor(
        protected http: ShAbstractHttpService,
        protected paginationService: ShPaginationService,
        protected middleware: MiddlewareService
    ) {}

    protected readonly defaultHeaders: HttpHeaders = new HttpHeaders().set('accept', 'application/hal+json');

    protected get recordsEndpoint(): string {
        return `v2/records`;
    }

    protected get postSearchEndpoint(): string {
        return `v2/records/search`;
    }

    protected getPostBody(recordSearchRequest: RecordSearchRequest): SearchParamsPostBody {
        return recordSearchRequest.toPostBody();
    }

    // #region Get Single
    public getRecord(fragmentId: Identifier, options?: RecordSearchOptions): Observable<ApiFragment> {
        const annotate = options?.annotate ?? true;
        const includeDeleted = options?.includeDeleted ?? false;
        const fields = options?.fields ?? [];

        const retry = options?.retry ?? false;
        const trackConsult = options?.trackConsult ?? false;

        const request = `${this.recordsEndpoint}/${fragmentId}`;
        let params = new HttpParams().append('includeDeleted', includeDeleted);

        fields.forEach(field => {
            params = params.append('fields', field);
        });

        return this.http.get<Metadata>(request, params, this.defaultHeaders, retry).pipe(
            map(result => result || []),
            map(result => new ApiFragment(result)),
            switchMap(record => (annotate ? this.annotateFragment(record) : of(record))),
            tap(record => {
                if (trackConsult) this.registerConsultation(record.getFragmentId());
            })
        );
    }
    // #endregion

    // #region Get paginated
    public getRecords(
        paginationInfo: PaginationInfo,
        sortInfo: SortInfo,
        query: ApiQuery
    ): Observable<PaginatedResults<ApiFragment>> {
        const recordSearchRequest: RecordSearchRequest = new RecordSearchRequest(query, paginationInfo, sortInfo);
        return this.getRecordsThroughPost(recordSearchRequest);
    }

    protected getRecordsThroughPost(
        recordSearchRequest: RecordSearchRequest
    ): Observable<PaginatedResults<ApiFragment>> {
        const request = `${this.postSearchEndpoint}`;

        if (recordSearchRequest.getNrOfResults() > 100) {
            return this.paginationService
                .getMultipleThroughPost<Metadata>(
                    request,
                    this.getPostBody(recordSearchRequest),
                    this.defaultHeaders,
                    recordSearchRequest.getNrOfResults()
                )
                .pipe(
                    switchMap((result: Metadata[]) => this.mapToRecordList(result)),
                    map(results => ({
                        paginationInfo: {
                            ...recordSearchRequest.toPaginationInfo(),
                            totalNumberOfResults: results.length
                        },
                        results
                    }))
                );
        }
        const params: SearchParamsPostBody = this.getPostBody(recordSearchRequest);

        return this.http
            .post<Paginated<Metadata>>(request, params, this.defaultHeaders)
            .pipe(switchMap(result => this.parsePaginatedApiResponse(result, recordSearchRequest.getNrOfResults())));
    }
    // #endregion

    // #region Get All
    public getAllRecords(
        query: ApiQuery,
        options?: RecordSearchOptions,
        sortInfo?: SortInfo
    ): Observable<ApiFragment[]> {
        const recordSearchRequest: RecordSearchRequest = new RecordSearchRequest(query, undefined, sortInfo);
        return this.getAllRecordsThroughPost(recordSearchRequest, options);
    }

    private getAllRecordsThroughPost(
        recordSearchRequest: RecordSearchRequest,
        options?: RecordSearchOptions
    ): Observable<ApiFragment[]> {
        const request = `${this.postSearchEndpoint}`;

        const annotate = options?.annotate ?? true;

        return this.paginationService
            .getAllThroughPost<Metadata>(request, this.getPostBody(recordSearchRequest), this.defaultHeaders)
            .pipe(switchMap((result: Metadata[]) => this.mapToRecordList(result, annotate)));
    }
    // #endregion

    // #region Exif
    public getExif(fragmentId: FragmentId | MediaObjectId | RecordId): Observable<Record<string, string>> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set('fields', 'Exif');
        const request = `${this.recordsEndpoint}/${fragmentId}?${params.toString()}`;
        return this.http.get<Metadata>(request).pipe(map(result => result.Technical.Exif || {}));
    }
    // #endregion

    // #region Rejections
    public getRejections(recordId: string): Observable<RejectionList[]> {
        return this.paginationService
            .getAll<RejectionList>(
                `${this.recordsEndpoint}/${recordId}/rejections`,
                new HttpParams(),
                new HttpHeaders()
            )
            .pipe(take(1), shareReplay());
    }
    // #endregion

    // #region Consult
    public registerConsultation(fragmentId: FragmentId | RecordId): void {
        const url: string = `${this.recordsEndpoint}/${fragmentId}/consult`;

        this.http
            .post(url, {})
            .pipe(
                take(1),
                catchError(err => {
                    console.error('Unable to register consultation', err);
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public registerDownload(fragmentId: FragmentId | RecordId): void {
        const url: string = `${this.recordsEndpoint}/${fragmentId}/direct-download`;

        this.http
            .post(url, {})
            .pipe(
                take(1),
                catchError(err => {
                    console.error('Unable to register download', err);
                    return EMPTY;
                })
            )
            .subscribe();
    }
    // #endregion

    // #region Middleware
    protected annotateFragment(apiFragment: ApiFragment): Observable<ApiFragment> {
        return this.middleware.annotate(apiFragment);
    }
    // #endregion

    // #region Utils
    private mapToRecordList(metadataList: Metadata[], annotate: boolean = true): Observable<ApiFragment[]> {
        return forkJoinAlwaysEmit(
            metadataList.map(result =>
                annotate ? this.annotateFragment(new ApiFragment(result)) : of(new ApiFragment(result))
            )
        );
    }

    private parsePaginatedApiResponse(
        apiResponse: Paginated<Metadata>,
        resultsPerPage: number
    ): Observable<PaginatedResults<ApiFragment>> {
        const paginationInfo: ResponsePaginationInfo = {
            totalNumberOfResults: apiResponse.TotalNrOfResults,
            page: apiResponse.StartIndex / Number(resultsPerPage),
            resultsPerPage: Number(resultsPerPage)
        };
        const fragments$: Observable<ApiFragment[]> = this.mapToRecordList(apiResponse.Results);

        return fragments$.pipe(
            map(fragments => ({
                paginationInfo,
                results: fragments
            }))
        );
    }

    public checkIfFragmentExists(
        fragmentId: FragmentId,
        includeLogicallyDeleted: boolean = false
    ): Observable<boolean> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'includeDeleted',
            Boolean(includeLogicallyDeleted).toString()
        );

        const request = `${this.recordsEndpoint}/${fragmentId}?${params.toString()}`;

        return this.http.get<boolean>(request).pipe(
            mapTo(true),
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    return of(false);
                }
                return throwError(error);
            })
        );
    }
    // #endregion
}
